import React, { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import {
  Loader2, Download, Users2, Sparkles, Mail, Archive, RotateCcw,
  Trash2, History, FlaskConical, MoreHorizontal, UserCircle2, Shield,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminLeads() {
  const { user, loading } = useAuth();
  const [summary, setSummary] = useState(null);
  const [waitlist, setWaitlist] = useState({ items: [], active_total: 0, archived_total: 0 });
  const [risk, setRisk] = useState({ items: [], active_total: 0, archived_total: 0 });
  const [scans, setScans] = useState({ items: [], active_total: 0, archived_total: 0 });
  const [users, setUsers] = useState({ items: [], active_total: 0, archived_total: 0 });
  const [audit, setAudit] = useState([]);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [view, setView] = useState("active"); // "active" | "archived"
  const [confirm, setConfirm] = useState(null); // {kind, action, item}

  const isAdmin = user?.role === "admin";

  const reload = useCallback(async () => {
    if (!isAdmin) return;
    setBusy(true);
    const arch = view === "archived";
    const calls = [
      ["summary",  api.get("/admin/leads/summary")],
      ["waitlist", api.get(`/admin/leads/waitlist?archived=${arch}`)],
      ["risk",     api.get(`/admin/leads/risk-score?archived=${arch}`)],
      ["scans",    api.get(`/admin/scans?archived=${arch}&limit=100`)],
      ["users",    api.get(`/admin/users?archived=${arch}&limit=200`)],
      ["audit",    api.get("/admin/audit-logs?limit=30")],
    ];
    const results = await Promise.allSettled(calls.map(([, p]) => p));
    const failed = [];
    results.forEach((r, i) => {
      const [key] = calls[i];
      if (r.status === "fulfilled") {
        const d = r.value.data;
        if (key === "summary") setSummary(d);
        if (key === "waitlist") setWaitlist(d);
        if (key === "risk") setRisk(d);
        if (key === "scans") setScans(d);
        if (key === "users") setUsers(d);
        if (key === "audit") setAudit(d?.items || []);
      } else {
        failed.push({ key, err: r.reason });
        // eslint-disable-next-line no-console
        console.warn(`[admin-leads] ${key} failed:`, formatApiError(r.reason));
      }
    });
    if (failed.length === results.length) {
      // every endpoint failed — surface a single toast (usually means auth/network)
      toast.error("Couldn't load admin data", {
        description: formatApiError(failed[0].err),
      });
    } else if (failed.length > 0) {
      toast.warning(`${failed.length} section${failed.length > 1 ? "s" : ""} unavailable`, {
        description: `${failed.map(f => f.key).join(", ")} — your backend may need a redeploy.`,
      });
    }
    setBusy(false);
  }, [isAdmin, view]);

  useEffect(() => { reload(); }, [reload]);

  if (loading || user === null) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
      </div>
    );
  }
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  const downloadCsv = async (path, fallbackName) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : fallbackName;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Export failed", { description: formatApiError(err) });
    }
  };

  const runAction = async () => {
    if (!confirm) return;
    const { kind, action, item } = confirm;
    const base =
      kind === "waitlist" ? `/admin/leads/waitlist/${item.id}` :
      kind === "risk" ? `/admin/leads/risk-score/${item.id}` :
      kind === "users" ? `/admin/users/${item.user_id}` :
      `/admin/scans/${item.id}`;
    try {
      if (action === "archive") {
        await api.post(`${base}/archive`);
        toast.success("Archived");
      } else if (action === "restore") {
        await api.post(`${base}/restore`);
        toast.success("Restored");
      } else if (action === "delete") {
        await api.delete(base);
        toast.success("Deleted permanently");
      }
      setConfirm(null);
      reload();
    } catch (err) {
      toast.error("Action failed", { description: formatApiError(err) });
    }
  };

  const filt = (rows, fields) =>
    !q
      ? rows
      : rows.filter((r) =>
          fields.some((f) => String(r[f] || "").toLowerCase().includes(q.toLowerCase()))
        );

  return (
    <div className="space-y-8" data-testid="admin-leads-page">
      <div>
        <p className="text-xs uppercase tracking-[0.22em] font-semibold text-blue-600">Admin</p>
        <h1 className="mt-1.5 font-display text-3xl font-semibold tracking-tight text-slate-900">
          Marketing leads
        </h1>
        <p className="mt-1.5 text-sm text-slate-600">
          Combined view of landing-page signups, risk-report unlocks, and admin
          scan management. Archive spam/test entries — permanent deletion is a
          two-step process for safety.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard icon={UserCircle2} label="Registered users" value={users?.active_total ?? 0}
                  sub={`${users?.archived_total || 0} archived`} testId="stat-users" />
        <StatCard icon={Users2} label="Active waitlist" value={summary?.waitlist?.total}
                  sub={`${summary?.waitlist?.archived || 0} archived`} testId="stat-waitlist" />
        <StatCard icon={Mail} label="Active risk leads" value={summary?.risk_leads?.total}
                  sub={`${summary?.risk_leads?.archived || 0} archived`} testId="stat-risk-leads" />
        <StatCard icon={Sparkles} label="Risk reports generated" value={summary?.risk_scores?.total} testId="stat-risk-scores" />
        <StatCard icon={FlaskConical} label="Active scans" value={scans?.active_total ?? 0}
                  sub={`${scans?.archived_total || 0} archived`} testId="stat-scans" />
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by email or name…"
              className="h-9 max-w-xs"
              data-testid="admin-search"
            />
            <div className="inline-flex rounded-md border border-slate-200 p-0.5 bg-slate-50">
              <ViewToggle current={view} value="active" onClick={() => setView("active")}>
                Active
              </ViewToggle>
              <ViewToggle current={view} value="archived" onClick={() => setView("archived")}>
                Archived
              </ViewToggle>
            </div>
          </div>
          <span className="text-xs text-slate-500">{busy ? "Loading…" : "Live data"}</span>
        </div>

        <Tabs defaultValue="users">
          <div className="px-5 pt-4">
            <TabsList className="bg-slate-100">
              <TabsTrigger value="users" data-testid="tab-users">
                Users · {users?.items?.length ?? 0}
              </TabsTrigger>
              <TabsTrigger value="waitlist" data-testid="tab-waitlist">
                Waitlist · {waitlist?.items?.length ?? 0}
              </TabsTrigger>
              <TabsTrigger value="risk" data-testid="tab-risk">
                Risk leads · {risk?.items?.length ?? 0}
              </TabsTrigger>
              <TabsTrigger value="scans" data-testid="tab-scans">
                Scans · {scans?.items?.length ?? 0}
              </TabsTrigger>
              <TabsTrigger value="audit" data-testid="tab-audit">
                <History className="h-3.5 w-3.5 mr-1" />
                Audit log
              </TabsTrigger>
            </TabsList>
          </div>

          {/* USERS */}
          <TabsContent value="users" className="px-0 pt-0">
            <div className="px-5 py-3 text-xs text-slate-500">
              Every registered account with its workspace, plan, lifetime scan count
              and last activity. Archive blocks the user from logging in; permanent
              delete cascades their workspace, scans, documents and reports.
            </div>
            <DataTable
              rows={filt(users?.items || [], ["email", "name", "workspace_name"])}
              columns={[
                { key: "created_at", label: "Joined", render: (v) => v ? new Date(v).toLocaleString() : "—" },
                { key: "email", label: "Email", render: (v, r) => (
                  <div>
                    <div className="font-medium text-slate-900 flex items-center gap-1.5">
                      {v}
                      {r.role === "admin" && (
                        <Shield className="h-3 w-3 text-blue-600" aria-label="admin" />
                      )}
                    </div>
                    <div className="text-xs text-slate-500">
                      {r.name || "—"} · {r.auth_provider}
                    </div>
                  </div>
                ) },
                { key: "workspace_name", label: "Workspace", render: (v) => v || "—" },
                { key: "plan", label: "Plan", render: (v, r) => (
                  <span className="inline-flex rounded-full bg-slate-100 text-slate-700 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5">
                    {v}{r.plan_cycle ? ` · ${r.plan_cycle.slice(0,3)}` : ""}
                  </span>
                ) },
                { key: "scan_count", label: "Scans", render: (v, r) => (
                  <div>
                    <div className="tabular-nums">{v}</div>
                    {r.last_scan_at && (
                      <div className="text-[10px] text-slate-500">
                        last {new Date(r.last_scan_at).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                ) },
              ]}
              empty={view === "archived" ? "No archived users." : "No registered users yet."}
              testId="users-table"
              renderRowActions={(row) =>
                row.role === "admin" ? (
                  <span className="text-[10px] text-slate-400 pr-2">protected</span>
                ) : (
                  <RowActions
                    view={view}
                    onArchive={() => setConfirm({ kind: "users", action: "archive", item: row })}
                    onRestore={() => setConfirm({ kind: "users", action: "restore", item: row })}
                    onDelete={() => setConfirm({ kind: "users", action: "delete", item: row })}
                    testIdPrefix={`user-${row.user_id}`}
                  />
                )
              }
            />
          </TabsContent>

          {/* WAITLIST */}
          <TabsContent value="waitlist" className="px-0 pt-0">
            <div className="flex justify-end px-5 py-3">
              <Button
                onClick={() => downloadCsv(
                  `/admin/leads/waitlist.csv?archived=${view === "archived"}`,
                  "waitlist.csv",
                )}
                className="rounded-md bg-blue-600 hover:bg-blue-700 text-white h-9"
                data-testid="export-waitlist"
              >
                <Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV
              </Button>
            </div>
            <DataTable
              rows={filt(waitlist?.items || [], ["name", "email", "business_type"])}
              columns={[
                { key: "created_at", label: "Joined", render: (v) => v ? new Date(v).toLocaleString() : "—" },
                { key: "email", label: "Email" },
                { key: "name", label: "Name" },
                { key: "business_type", label: "Type", render: (v) => (v || "").replace("_", " ") },
                { key: "employees", label: "Size" },
              ]}
              empty={view === "archived" ? "No archived waitlist entries." : "No active waitlist signups."}
              testId="waitlist-table"
              renderRowActions={(row) => (
                <RowActions
                  view={view}
                  onArchive={() => setConfirm({ kind: "waitlist", action: "archive", item: row })}
                  onRestore={() => setConfirm({ kind: "waitlist", action: "restore", item: row })}
                  onDelete={() => setConfirm({ kind: "waitlist", action: "delete", item: row })}
                  testIdPrefix={`waitlist-${row.id}`}
                />
              )}
            />
          </TabsContent>

          {/* RISK LEADS */}
          <TabsContent value="risk" className="px-0 pt-0">
            <div className="flex justify-end px-5 py-3">
              <Button
                onClick={() => downloadCsv(
                  `/admin/leads/risk-score.csv?archived=${view === "archived"}`,
                  "risk-leads.csv",
                )}
                className="rounded-md bg-blue-600 hover:bg-blue-700 text-white h-9"
                data-testid="export-risk"
              >
                <Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV
              </Button>
            </div>
            <DataTable
              rows={filt(risk?.items || [], ["email"])}
              columns={[
                { key: "last_seen", label: "Last unlock", render: (v) => v ? new Date(v).toLocaleString() : "—" },
                { key: "first_seen", label: "First seen", render: (v) => v ? new Date(v).toLocaleDateString() : "—" },
                { key: "email", label: "Email" },
                { key: "unlocks", label: "Unlocks" },
              ]}
              empty={view === "archived" ? "No archived risk leads." : "No active risk-report unlocks."}
              testId="risk-table"
              renderRowActions={(row) => (
                <RowActions
                  view={view}
                  onArchive={() => setConfirm({ kind: "risk", action: "archive", item: row })}
                  onRestore={() => setConfirm({ kind: "risk", action: "restore", item: row })}
                  onDelete={() => setConfirm({ kind: "risk", action: "delete", item: row })}
                  testIdPrefix={`risk-${row.id}`}
                />
              )}
            />
          </TabsContent>

          {/* SCANS */}
          <TabsContent value="scans" className="px-0 pt-0">
            <div className="px-5 py-3 text-xs text-slate-500">
              Cross-workspace admin view. Useful for cleaning up test scans during
              development. Archive moves a scan out of dashboards; permanent delete
              also removes the scan's issues.
            </div>
            <DataTable
              rows={filt(scans?.items || [], ["document_name", "workspace_name", "created_by_name"])}
              columns={[
                { key: "created_at", label: "Created", render: (v) => v ? new Date(v).toLocaleString() : "—" },
                { key: "document_name", label: "Document" },
                { key: "workspace_name", label: "Workspace" },
                { key: "score", label: "Score", render: (v) => `${v}/100` },
                { key: "level", label: "Level" },
                { key: "status", label: "Status" },
                { key: "cost_usd", label: "Cost", render: (v) => v == null ? "—" : `$${Number(v).toFixed(5)}` },
              ]}
              empty={view === "archived" ? "No archived scans." : "No active scans."}
              testId="scans-table"
              renderRowActions={(row) => (
                <RowActions
                  view={view}
                  onArchive={() => setConfirm({ kind: "scans", action: "archive", item: row })}
                  onRestore={() => setConfirm({ kind: "scans", action: "restore", item: row })}
                  onDelete={() => setConfirm({ kind: "scans", action: "delete", item: row })}
                  testIdPrefix={`scan-${row.id}`}
                />
              )}
            />
          </TabsContent>

          {/* AUDIT LOG */}
          <TabsContent value="audit" className="px-0 pt-0">
            <div className="px-5 py-3 text-xs text-slate-500">
              Last 30 admin actions (archives, restores, hard deletes, manual upgrades).
            </div>
            <DataTable
              rows={audit}
              columns={[
                { key: "created_at", label: "When", render: (v) => v ? new Date(v).toLocaleString() : "—" },
                { key: "actor_email", label: "Admin" },
                { key: "action", label: "Action", render: (v) => <ActionBadge action={v} /> },
                { key: "target_kind", label: "Kind" },
                { key: "target_id", label: "Target", render: (v) => (
                  <span className="font-mono text-xs text-slate-500 truncate inline-block max-w-[200px]">{v}</span>
                ) },
              ]}
              empty="No admin actions yet."
              testId="audit-table"
            />
          </TabsContent>
        </Tabs>
      </div>

      <ConfirmDialog confirm={confirm} onClose={() => setConfirm(null)} onConfirm={runAction} />
    </div>
  );
}

function ViewToggle({ current, value, onClick, children }) {
  const active = current === value;
  return (
    <button
      onClick={onClick}
      data-testid={`view-${value}`}
      className={
        "px-3 h-8 rounded text-xs font-medium transition-colors " +
        (active ? "bg-white shadow-sm text-slate-900" : "text-slate-600 hover:text-slate-900")
      }
    >
      {children}
    </button>
  );
}

function StatCard({ icon: Icon, label, value, sub, testId }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500">{label}</div>
        <Icon className="h-4 w-4 text-slate-400" />
      </div>
      <div className="mt-2 font-display text-3xl font-semibold tabular-nums text-slate-900">
        {value ?? "—"}
      </div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

function RowActions({ view, onArchive, onRestore, onDelete, testIdPrefix }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="h-8 w-8 rounded-md hover:bg-slate-100 inline-flex items-center justify-center"
          data-testid={`${testIdPrefix}-actions`}
        >
          <MoreHorizontal className="h-4 w-4 text-slate-500" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        {view === "active" ? (
          <DropdownMenuItem onClick={onArchive} data-testid={`${testIdPrefix}-archive`}>
            <Archive className="h-3.5 w-3.5 mr-2 text-amber-600" /> Archive
          </DropdownMenuItem>
        ) : (
          <>
            <DropdownMenuItem onClick={onRestore} data-testid={`${testIdPrefix}-restore`}>
              <RotateCcw className="h-3.5 w-3.5 mr-2 text-blue-600" /> Restore
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={onDelete}
              className="text-rose-600 focus:text-rose-700 focus:bg-rose-50"
              data-testid={`${testIdPrefix}-delete`}
            >
              <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete forever
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ConfirmDialog({ confirm, onClose, onConfirm }) {
  const open = !!confirm;
  const { action, item, kind } = confirm || {};
  const isDestructive = action === "delete";
  const label = item?.email || item?.document_name || item?.id || "this entry";

  const isUser = kind === "users";

  const title =
    action === "archive" ? (isUser ? "Suspend user?" : "Archive entry?") :
    action === "restore" ? (isUser ? "Restore user?" : "Restore entry?") :
    "Permanently delete?";

  const desc =
    action === "archive" ? (
      isUser ? (
        <>
          <span className="font-medium">{label}</span> will be blocked from logging
          in immediately. Their workspaces, scans and reports remain untouched. You
          can restore the account from the Archived view at any time. Logged in the
          audit trail.
        </>
      ) : (
      <>
        <span className="font-medium">{label}</span> will be hidden from active
        views and CSV exports. You can restore it from the Archived tab at any
        time. This action is logged in the audit trail.
      </>
      )
    ) : action === "restore" ? (
      <>
        <span className="font-medium">{label}</span> will be {isUser ? "re-enabled and can sign in again" : "returned to active views and exports"}.
      </>
    ) : (
      <>
        <span className="font-medium">{label}</span> will be{" "}
        <span className="font-semibold text-rose-600">permanently deleted</span>{" "}
        from the database. This cannot be undone.
        {kind === "scans" && " The scan's findings/issues will also be removed."}
        {isUser && (
          <>
            {" "}This will also delete <b>all of their workspaces, scans, documents,
            reports and notifications</b>.
          </>
        )}
      </>
    );

  return (
    <AlertDialog open={open} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent data-testid="confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle data-testid="confirm-title">{title}</AlertDialogTitle>
          <AlertDialogDescription className="text-slate-600">
            {desc}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="confirm-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            data-testid="confirm-action"
            className={
              isDestructive
                ? "bg-rose-600 hover:bg-rose-700 text-white"
                : "bg-blue-600 hover:bg-blue-700 text-white"
            }
          >
            {action === "archive" && "Archive"}
            {action === "restore" && "Restore"}
            {action === "delete" && "Delete forever"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function ActionBadge({ action }) {
  const map = {
    archive: "bg-amber-50 text-amber-700 ring-amber-200",
    restore: "bg-blue-50 text-blue-700 ring-blue-200",
    hard_delete: "bg-rose-50 text-rose-700 ring-rose-200",
    manual_upgrade: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  };
  const cls = map[action] || "bg-slate-50 text-slate-700 ring-slate-200";
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold ring-1 ring-inset ${cls}`}>
      {action.replace("_", " ")}
    </span>
  );
}

function DataTable({ rows, columns, empty, testId, renderRowActions }) {
  return (
    <div className="overflow-x-auto" data-testid={testId}>
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-[0.12em] text-slate-500 font-semibold">
          <tr>
            {columns.map((c) => <th key={c.key} className="px-5 py-3">{c.label}</th>)}
            {renderRowActions && <th className="px-5 py-3 w-12 text-right" />}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.length === 0 && (
            <tr><td colSpan={columns.length + (renderRowActions ? 1 : 0)} className="px-5 py-10 text-center text-slate-500">{empty}</td></tr>
          )}
          {rows.map((r, idx) => (
            <tr key={r.id || idx} className="hover:bg-slate-50">
              {columns.map((c) => (
                <td key={c.key} className="px-5 py-3 text-slate-700">
                  {c.render ? c.render(r[c.key], r) : (r[c.key] ?? "—")}
                </td>
              ))}
              {renderRowActions && (
                <td className="px-5 py-3 text-right">
                  {renderRowActions(r)}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
